import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/*
 * This hook is used to get the current back URL and the next page's back URL.
 * It returns an object with following properties:
 * - currentBackURL: The current back URL from the search parameters.
 * - decodedCurrentBackURL: The decoded version of the current back URL.
 * - nextPageBackURL: The next page's back URL, which is the current location's pathname and search.
 * - encodedNextPageBackURL: The encoded version of the next page's back URL.
 * * It also provides a function to append the backURL parameter to a given URL.
 * This is useful for navigation purposes, especially in scenarios where you want to return to a previous page.
 */
export const useBackURL = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const backURLConfig = useMemo(() => {
    const currentBackURL = searchParams.get('backURL');
    const decodedCurrentBackURL = currentBackURL ? decodeURIComponent(currentBackURL) : null;
    const nextPageBackURL = location.pathname + location.search;
    const encodedNextPageBackURL = encodeURIComponent(nextPageBackURL);

    return { currentBackURL, decodedCurrentBackURL, nextPageBackURL, encodedNextPageBackURL };
  }, [location.pathname, location.search, searchParams]);

  // Function to append the backURL parameter to a given URL
  const appendBackURLParam = useCallback(
    (url: string) => {
      //check if url has search params
      const hasSearchParams = url.includes('?');
      const newURL = `${url}${hasSearchParams ? '&' : '?'}backURL=${backURLConfig.encodedNextPageBackURL}`;
      return newURL;
    },
    [backURLConfig.encodedNextPageBackURL]
  );

  return { ...backURLConfig, appendBackURLParam };
};
